import { useCallback, useRef } from 'react';
import type { ProcessNode } from './graphTypes';

interface UseProcessClipboardArgs {
  /** Current React-Flow selection — drives what `copySelected` snapshots. */
  readonly selectedNodeIds: readonly string[];
  /** Live nodes array (the source of truth for snapshots). */
  readonly nodes: ProcessNode[];
  /**
   * Mutator that appends a paste batch to the graph. Reuses
   * {@code useProcessGraph.pasteNodes} so the clipboard hook stays a
   * thin coordinator on top of the existing graph mutations.
   */
  readonly pasteNodes: (
    snapshots: readonly ProcessNode[],
    target?: { x: number; y: number },
  ) => void;
  /**
   * When true, both methods are no-ops. Wired from the editor's
   * effective read-only flag so Published / actively-running canvases
   * never receive a paste even if a stray keypress slips past the
   * focus guard.
   */
  readonly disabled: boolean;
  /**
   * Best-effort canvas cursor position lookup, returned in canvas-
   * world coordinates. The hook prefers this when present so paste
   * lands where the user expects; a {@code null} return (cursor off
   * the canvas, no rfInstance yet) falls back to the +40/+40 offset
   * baked into {@code cloneNodeForDuplicate}.
   */
  readonly getCursorWorldPos?: () => { x: number; y: number } | null;
}

interface UseProcessClipboardReturn {
  /** Snapshot the current selection into the editor-internal clipboard. */
  copySelected: () => void;
  /** Paste the most recent clipboard payload onto the canvas. */
  paste: () => void;
}

/**
 * Editor-internal clipboard for "copy / paste selected nodes". Uses a
 * private ref instead of {@code navigator.clipboard} on purpose:
 *
 * <ul>
 *   <li>The user's expectation is "copy these nodes within this editor",
 *       not "serialize them to text and put them on the OS clipboard".
 *       Going via the OS clipboard would clobber any plain-text the
 *       user had stored, prompt for clipboard permissions, and
 *       round-trip through string serialisation that we don't need.</li>
 *   <li>Cross-tab / cross-app paste is explicitly out of scope for
 *       v1 (see the original story).</li>
 * </ul>
 *
 * The clipboard is per-editor-instance — it's reset whenever the page
 * unmounts (hook teardown), which matches the user mental model of
 * "this clipboard belongs to this process editor".
 */
export function useProcessClipboard(
  args: UseProcessClipboardArgs,
): UseProcessClipboardReturn {
  // We hold the most recent snapshot here. The ref is intentionally
  // mutable across renders so paste-after-rerender keeps working
  // without re-keying React state.
  const clipboardRef = useRef<readonly ProcessNode[] | null>(null);

  const { selectedNodeIds, nodes, pasteNodes, disabled, getCursorWorldPos } =
    args;

  const copySelected = useCallback(() => {
    if (disabled || selectedNodeIds.length === 0) return;
    const idSet = new Set(selectedNodeIds);
    // Deep-clone so subsequent edits to the live nodes don't bleed
    // into the clipboard payload. structuredClone preserves the
    // nested `data` shape (ports, instructions, etc) without us
    // having to maintain a custom clone walker.
    clipboardRef.current = nodes
      .filter((n) => idSet.has(n.id))
      .map((n) => structuredClone({ ...n, selected: false }));
  }, [disabled, selectedNodeIds, nodes]);

  const paste = useCallback(() => {
    if (disabled) return;
    const payload = clipboardRef.current;
    if (!payload || payload.length === 0) return;
    const target = getCursorWorldPos?.() ?? undefined;
    pasteNodes(payload, target ?? undefined);
  }, [disabled, pasteNodes, getCursorWorldPos]);

  return { copySelected, paste };
}
